/**
 * All shared, cross-chart state lives here: the branch, the mutagen domain
 * (zoom), the cursor, the selection and the model parameters. The four charts
 * are pure consumers — they never own any of it, which is what keeps them
 * linked.
 *
 * The domain is anchored at 0: zooming only ever moves the upper bound, so
 * every chart always reads left-to-right from "nothing fed yet".
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  BASE_RULESET_ORDER,
  DEFAULTS,
  DEFAULT_MAX_EVO,
  DEFAULT_RULESET_ID,
  DEFAULT_SCENARIO_PARAMS,
  DIFFICULTIES,
  RULESETS,
  RULESET_ORDER,
  buildSeries,
  clampScenario,
  decodeScenario,
  encodeScenario,
  evaluateAt,
  evoAxisTicks,
  isDefaultScenario,
  makeScenarioRuleset,
  maxMutagenFor,
  milestoneCrossings,
  mutagenToReachEvo,
  summariseRange,
  type Domain,
  type MilestoneCrossing,
  type RangeSummary,
  type Ruleset,
  type RulesetId,
  type Sample,
  type ScenarioParams,
  type SeriesParams,
  type UnitClass,
} from '../model';

/** Hard lower bound for the upper edge of the domain, in raw mutagen. */
const MIN_MAX_MUTAGEN = 1e-3;

/** Shareable deep link: `?branch=live,testing3v3`. */
const BRANCH_PARAM = 'branch';

/**
 * `?tune=1` — the balance tuner.
 *
 * Off by default, and off is the plain lab: two transcribed branches, four
 * charts, nothing on screen that does not exist in a scenario file somewhere.
 * On, the page grows a third curve whose constants are all editable, swaps the
 * biter-strength chart for the instant/passive ratio the tuning is aimed at,
 * and puts the parameters in the address bar so a candidate can be linked to.
 */
const TUNE_PARAM = 'tune';

/** `?sc=277,0.3,1,25,2,25,125` — the scenario's constants, in `encodeScenario` order. */
const SCENARIO_PARAM = 'sc';

function readParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function tuningFromUrl(): boolean {
  const value = readParam(TUNE_PARAM);
  return value !== null && value !== '0' && value !== 'false';
}

function scenarioFromUrl(): ScenarioParams {
  return decodeScenario(readParam(SCENARIO_PARAM));
}

/**
 * Stroke pattern per selected branch. Colour already means *which quantity*, so
 * the branch has to be a different channel or the two would fight; dash is the
 * one that survives being drawn over a grid at 2px.
 *
 * Only used while comparing. With one branch selected there is nothing to tell
 * apart, so a chart's own `dashed` lines keep their meaning.
 */
const BRANCH_DASH: (string | undefined)[] = [undefined, '6 4', '2 3'];

function branchesFromUrl(): RulesetId[] {
  const param = readParam(BRANCH_PARAM);
  // Turning the tuner on with no explicit branch list selects all three: the
  // scenario is only worth looking at against what it is replacing.
  if (!param) return tuningFromUrl() ? [...RULESET_ORDER] : [DEFAULT_RULESET_ID];
  const ids = param
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part in RULESETS) as RulesetId[];
  return ids.length ? canonical(ids) : [DEFAULT_RULESET_ID];
}

/**
 * Selections are held in one canonical order rather than in click order, so
 * which branch is *primary* is a property of what is selected and not of how
 * you got there. Clicking the same two branches in either order gives the same
 * view.
 */
function canonical(ids: RulesetId[]): RulesetId[] {
  return RULESET_ORDER.filter((id) => ids.includes(id));
}

/**
 * One branch's view of the shared x domain, cursor and selection.
 *
 * Every branch is evaluated over the *same* mutagen domain — that is the whole
 * point of comparing: the same science sent, and what each branch does with it.
 * The primary branch owns everything that cannot be drawn twice (the zoom
 * ceiling, the evolution axis, the milestone guides).
 */
export interface BranchView {
  ruleset: Ruleset;
  params: SeriesParams;
  series: Sample[];
  cursorSample: Sample | null;
  selectionSummary: RangeSummary | null;
  /** `strokeDasharray` for this branch while comparing; undefined is solid. */
  dash: string | undefined;
  /** True for the branch that owns the domain, the evo axis and the guides. */
  primary: boolean;
}

export interface LabValue {
  // ---- inputs -------------------------------------------------------------
  /**
   * The primary branch — the first selected in canonical order. It owns the
   * zoom domain, the evolution axis and the milestone guides, which is
   * everything there can only be one of.
   */
  ruleset: Ruleset;
  /** Every selected branch, primary first. Length 1 unless comparing. */
  rulesetIds: RulesetId[];
  /** Per-branch series, cursor and selection. One entry per selected branch. */
  views: BranchView[];
  /** More than one branch selected. */
  comparing: boolean;
  /** Add or remove a branch. Refuses to deselect the last one. */
  toggleRulesetId: (id: RulesetId) => void;
  setRulesetIds: (ids: RulesetId[]) => void;
  /** Branches the switch offers — the scenario only appears while tuning. */
  availableRulesetIds: RulesetId[];
  /** Look a branch up by id, with the *current* scenario rather than the default one. */
  rulesetFor: (id: RulesetId) => Ruleset;

  // ---- the tuner ----------------------------------------------------------
  /** `?tune=1`. Adds the scenario curve, its controls and the ratio chart. */
  tuning: boolean;
  setTuning: (value: boolean) => void;
  scenario: ScenarioParams;
  /** Partial update, so a control only states the field it owns. */
  setScenario: (patch: Partial<ScenarioParams>) => void;
  resetScenario: () => void;
  /** The scenario as a ruleset — the same instance the views are drawn from. */
  scenarioRuleset: Ruleset;
  difficultyIndex: number | null;
  difficulty: number;
  setDifficultyIndex: (index: number | null) => void;
  setCustomDifficulty: (value: number) => void;
  playerCount: number;
  setPlayerCount: (value: number) => void;
  /**
   * `storage.max_reanim_thresh`. Fixed at 250 — init.lua assigns it once and
   * nothing in the scenario ever writes to it again, so it is not a control.
   */
  maxReanimThresh: number;
  unitClass: UnitClass;
  setUnitClass: (value: UnitClass) => void;

  // ---- derived data, for the primary branch -------------------------------
  // Kept alongside `views` so anything that is only ever about one branch —
  // the axes, the guides, the zoom readout — can stay written that way.
  params: SeriesParams;
  series: Sample[];
  crossings: MilestoneCrossing[];
  evoTicks: { evo: number; mutagen: number }[];

  // ---- shared view state --------------------------------------------------
  /** Always [0, max]. */
  domain: Domain;
  /** Upper edge of the domain, in raw mutagen. */
  maxMutagen: number;
  /** Evolution at the upper edge — what the zoom control reports. */
  maxEvo: number;
  setMaxEvo: (maxEvo: number) => void;
  /** Scale the upper bound; > 1 zooms out, < 1 zooms in. */
  zoomBy: (factor: number) => void;
  resetZoom: () => void;
  zoomToSelection: () => void;
  /** Extend the upper bound if needed so `mutagen` is on screen. */
  ensureVisible: (mutagen: number) => void;

  cursor: number | null;
  cursorSample: Sample | null;
  setCursor: (mutagen: number | null) => void;

  /** Always ordered `[start, end]` with `start <= end`. */
  selection: Domain | null;
  selectionSummary: RangeSummary | null;
  /** Stores the range normalised, so `selection[0]` is always the start. */
  setSelection: (range: Domain | null) => void;

  /** Pointer-drag plumbing shared by every chart. */
  dragging: boolean;
  beginDrag: (mutagen: number) => void;
  updateDrag: (mutagen: number) => void;
  endDrag: () => void;
}

const LabContext = createContext<LabValue | null>(null);

export function useLab(): LabValue {
  const value = useContext(LabContext);
  if (!value) throw new Error('useLab must be used inside <LabProvider>');
  return value;
}

const clamp = (value: number, lo: number, hi: number) => Math.min(Math.max(value, lo), hi);

export function LabProvider({ children }: { children: ReactNode }) {
  const [rulesetIds, setRulesetIdsRaw] = useState<RulesetId[]>(branchesFromUrl);
  const [difficultyIndex, setDifficultyIndex] = useState<number | null>(DEFAULTS.difficultyIndex);
  const [customDifficulty, setCustomDifficulty] = useState(0.75);
  const [playerCount, setPlayerCount] = useState(DEFAULTS.playerCount);
  const [unitClass, setUnitClass] = useState<UnitClass>('normal');
  const [tuning, setTuning] = useState(tuningFromUrl);
  const [scenario, setScenarioRaw] = useState<ScenarioParams>(scenarioFromUrl);
  const maxReanimThresh = DEFAULTS.maxReanimThresh;

  // One instance per parameter set, and everything downstream reads *this* one.
  // Rebuilding it per render would defeat every memo below it, since the views
  // are keyed on object identity rather than on the numbers inside.
  const scenarioRuleset = useMemo(() => makeScenarioRuleset(scenario), [scenario]);

  const rulesetFor = useCallback(
    (id: RulesetId): Ruleset => (id === 'scenario' ? scenarioRuleset : RULESETS[id]),
    [scenarioRuleset],
  );

  const availableRulesetIds = tuning ? RULESET_ORDER : BASE_RULESET_ORDER;

  const rulesetId = rulesetIds[0];
  const ruleset = rulesetFor(rulesetId);
  const comparing = rulesetIds.length > 1;

  const [maxMutagen, setMaxMutagen] = useState(() =>
    mutagenToReachEvo(RULESETS[branchesFromUrl()[0]], DEFAULT_MAX_EVO, 0),
  );
  const [cursor, setCursor] = useState<number | null>(null);
  const [selection, setSelectionRaw] = useState<Domain | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragAnchor = useRef<number | null>(null);

  /**
   * Every selection is stored ordered. Dragging right-to-left and typing an end
   * below the start both produce a reversed pair, and without this the pane's
   * "start" and "end" fields would silently swap under the user.
   */
  const setSelection = useCallback((range: Domain | null) => {
    setSelectionRaw(
      range ? [Math.min(range[0], range[1]), Math.max(range[0], range[1])] : null,
    );
  }, []);

  const difficulty =
    difficultyIndex === null
      ? customDifficulty
      : (DIFFICULTIES.find((d) => d.index === difficultyIndex)?.value ?? 0.75);

  const domain = useMemo<Domain>(() => [0, maxMutagen], [maxMutagen]);

  const paramsByBranch = useMemo<SeriesParams[]>(
    () => rulesetIds.map((id) => ({ ruleset: rulesetFor(id), playerCount, maxReanimThresh })),
    [rulesetIds, rulesetFor, playerCount, maxReanimThresh],
  );

  // Sampling is the expensive part and it depends on none of the pointer
  // state, so it is memoised on its own. Moving the cursor must not re-sample
  // one branch, let alone two.
  const seriesByBranch = useMemo(
    () => paramsByBranch.map((p) => buildSeries(domain, p, 320, 160)),
    [domain, paramsByBranch],
  );

  const params = paramsByBranch[0];
  const series = seriesByBranch[0];
  const crossings = useMemo(() => milestoneCrossings(params), [params]);
  const evoTicks = useMemo(() => evoAxisTicks(domain, params), [domain, params]);

  const maxEvo = useMemo(() => evaluateAt(maxMutagen, params).evo, [maxMutagen, params]);

  // A zero-width selection is a *point* selection, not an empty one: clicking a
  // chart without dragging pins one mutagen value across all four.
  const views = useMemo<BranchView[]>(
    () =>
      paramsByBranch.map((p, index) => ({
        ruleset: p.ruleset,
        params: p,
        series: seriesByBranch[index],
        cursorSample: cursor === null ? null : evaluateAt(cursor, p),
        selectionSummary: selection ? summariseRange(selection[0], selection[1], p) : null,
        // Indexed, not `??`-defaulted: the primary's dash is *legitimately*
        // undefined (solid), which a nullish fallback would swallow.
        dash: BRANCH_DASH[Math.min(index, BRANCH_DASH.length - 1)],
        primary: index === 0,
      })),
    [paramsByBranch, seriesByBranch, cursor, selection],
  );

  const cursorSample = views[0].cursorSample;
  const selectionSummary = views[0].selectionSummary;

  const ceiling = maxMutagenFor(ruleset);

  const setMaxEvo = useCallback(
    (maxEvo: number) => {
      setMaxMutagen(clamp(mutagenToReachEvo(ruleset, maxEvo, 0), MIN_MAX_MUTAGEN, ceiling));
    },
    [ruleset, ceiling],
  );

  const resetZoom = useCallback(() => {
    setMaxMutagen(mutagenToReachEvo(ruleset, DEFAULT_MAX_EVO, 0));
  }, [ruleset]);

  const zoomBy = useCallback(
    (factor: number) => {
      setMaxMutagen((current) => clamp(current * factor, MIN_MAX_MUTAGEN, ceiling));
    },
    [ceiling],
  );

  const ensureVisible = useCallback(
    (mutagen: number) => {
      setMaxMutagen((current) =>
        mutagen <= current ? current : clamp(mutagen * 1.05, MIN_MAX_MUTAGEN, ceiling),
      );
    },
    [ceiling],
  );

  /**
   * Changing the **primary** branch holds the evolution window, not the mutagen
   * window.
   *
   * The two branches charge wildly different amounts of mutagen for the same
   * evolution — 100% costs 337 on live and 277 here, 330% costs 4.1k there and
   * 14.8k here — so keeping the mutagen domain fixed would swing the view to a
   * different part of the game and the change would read as noise. Holding
   * evolution instead makes it an A/B: the same stretch of the game, and what
   * moves is what the branch actually changed. The selection is carried across
   * the same way, and the cursor is dropped rather than re-pinned to a mutagen
   * value that now means something else.
   *
   * Merely *adding* a branch to the comparison moves nothing: the primary is
   * unchanged, so the domain it owns is still the right one and the new branch
   * is drawn over it.
   */
  const setRulesetIds = useCallback(
    (ids: RulesetId[]) => {
      const next = canonical(ids);
      // The charts have to draw something; an empty selection is not a state.
      if (!next.length) return;
      if (next.length === rulesetIds.length && next.every((id, i) => id === rulesetIds[i])) return;

      if (next[0] !== rulesetId) {
        const nextPrimary = rulesetFor(next[0]);
        const nextCeiling = maxMutagenFor(nextPrimary);
        const toNext = (mutagen: number) =>
          clamp(mutagenToReachEvo(nextPrimary, ruleset.evoForMutagen(mutagen), 0), 0, nextCeiling);

        setMaxMutagen(clamp(toNext(maxMutagen), MIN_MAX_MUTAGEN, nextCeiling));
        if (selection) setSelectionRaw([toNext(selection[0]), toNext(selection[1])]);
        setCursor(null);
      }
      setRulesetIdsRaw(next);
    },
    [rulesetIds, rulesetId, ruleset, rulesetFor, maxMutagen, selection],
  );

  const toggleRulesetId = useCallback(
    (id: RulesetId) => {
      const has = rulesetIds.includes(id);
      // Deselecting the last branch would leave nothing to draw, so the last
      // one selected is sticky rather than disabled — clicking it is a no-op.
      if (has && rulesetIds.length === 1) return;
      setRulesetIds(has ? rulesetIds.filter((entry) => entry !== id) : [...rulesetIds, id]);
    },
    [rulesetIds, setRulesetIds],
  );

  const setScenario = useCallback((patch: Partial<ScenarioParams>) => {
    // Clamped on the merged result, not on the patch: `n` lives in `(1, 1/p)`,
    // so moving `p` alone can put a perfectly good `n` out of its window.
    setScenarioRaw((current) => clampScenario({ ...current, ...patch }));
  }, []);

  const resetScenario = useCallback(() => setScenarioRaw(DEFAULT_SCENARIO_PARAMS), []);

  /**
   * Turning the tuner off has to take the scenario curve with it — leaving it
   * selected would draw a third line with no controls and no branch button to
   * clear it. Turning it on adds the curve to whatever is already selected
   * rather than replacing the view.
   */
  const setTuningAndSelection = useCallback(
    (next: boolean) => {
      setTuning(next);
      if (next) {
        if (!rulesetIds.includes('scenario')) setRulesetIds([...rulesetIds, 'scenario']);
      } else if (rulesetIds.includes('scenario')) {
        const rest = rulesetIds.filter((id) => id !== 'scenario');
        setRulesetIds(rest.length ? rest : [DEFAULT_RULESET_ID]);
      }
    },
    [rulesetIds, setRulesetIds],
  );

  // Keep the selection in the URL so a view can be linked to, and so a reload
  // stays on the branches you were reading. The tuner writes its flag and its
  // constants into the same URL, so a tuned candidate is one paste.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const value = rulesetIds.join(',');
    if (value === DEFAULT_RULESET_ID) url.searchParams.delete(BRANCH_PARAM);
    else url.searchParams.set(BRANCH_PARAM, value);

    if (tuning) url.searchParams.set(TUNE_PARAM, '1');
    else url.searchParams.delete(TUNE_PARAM);

    // Defaults stay out of the URL: a link with no `sc=` is a link to whatever
    // the defaults are *now*, which is what makes them cheap to retune.
    if (tuning && !isDefaultScenario(scenario)) {
      url.searchParams.set(SCENARIO_PARAM, encodeScenario(scenario));
    } else {
      url.searchParams.delete(SCENARIO_PARAM);
    }

    if (url.toString() !== window.location.href) {
      window.history.replaceState(null, '', url.toString());
    }
  }, [rulesetIds, tuning, scenario]);

  const zoomToSelection = useCallback(() => {
    if (!selection) return;
    const hi = selection[1];
    if (hi < MIN_MAX_MUTAGEN) return;
    setMaxMutagen(clamp(hi * 1.05, MIN_MAX_MUTAGEN, ceiling));
  }, [selection, ceiling]);

  const beginDrag = useCallback(
    (mutagen: number) => {
      dragAnchor.current = mutagen;
      setDragging(true);
      setSelection([mutagen, mutagen]);
    },
    [setSelection],
  );

  const updateDrag = useCallback(
    (mutagen: number) => {
      if (dragAnchor.current === null) return;
      setSelection([dragAnchor.current, mutagen]);
    },
    [setSelection],
  );

  const endDrag = useCallback(() => {
    setDragging(false);
    dragAnchor.current = null;
    // Whatever was dragged stands, including a zero-width click.
  }, []);

  const value: LabValue = {
    ruleset,
    rulesetIds,
    views,
    comparing,
    toggleRulesetId,
    setRulesetIds,
    availableRulesetIds,
    rulesetFor,
    tuning,
    setTuning: setTuningAndSelection,
    scenario,
    setScenario,
    resetScenario,
    scenarioRuleset,
    difficultyIndex,
    difficulty,
    setDifficultyIndex,
    setCustomDifficulty,
    playerCount,
    setPlayerCount,
    maxReanimThresh,
    unitClass,
    setUnitClass,
    params,
    series,
    crossings,
    evoTicks,
    domain,
    maxMutagen,
    maxEvo,
    setMaxEvo,
    zoomBy,
    resetZoom,
    zoomToSelection,
    ensureVisible,
    cursor,
    cursorSample,
    setCursor,
    selection,
    selectionSummary,
    setSelection,
    dragging,
    beginDrag,
    updateDrag,
    endDrag,
  };

  return <LabContext.Provider value={value}>{children}</LabContext.Provider>;
}
